import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ObjectiveCategory, ObjectiveSummaryView } from '@contracts';
import { useDomainApi, useDomainVersion } from '@ui/shared/domain/DomainApiContext';
import { ChevronLeft, Plus, Target } from '@ui/shared/icons';
import { SkeletonBlock } from '@ui/shared/primitives/Skeleton';
import { RailProgressLine } from '@ui/shared/primitives/RailProgressLine';
import { formatShortDate } from '@ui/shared/format/date';
import { inputStyle } from '@ui/shared/primitives/FormControls';
import { CATEGORY_OPTIONS, SegmentedControl } from '@ui/desktop/objective-form/FormPrimitives';

type StatusFilter = 'all' | 'active' | 'on_hold' | 'draft' | 'archived';
type SortBy = 'target_date' | 'alphabetical' | 'progress';
type CategoryKey = ObjectiveCategory | 'uncategorized';

/**
 * All Objectives — desktop tree, repurposed as the full browse/manage
 * catalog (owner direction, following the Objective Detail/Edit
 * consolidation): now that Dashboard owns "active objectives at a glance
 * with a realism snapshot" and viewing/editing an objective is one screen,
 * this screen's own job is search/filter/sort across EVERY objective —
 * including 'draft' and 'on_hold' states, which the Phase 4 shape of this
 * screen silently dropped entirely (a real gap: the state machine grew to
 * four values in Phase 5, but this screen still only ever grouped
 * active/archived). Status tabs now cover all four states explicitly.
 *
 * Still governed by the same owner correction that reverted the Phase 4
 * round: no stat-card strip, pace-bar/pill/insight treatment, realism side
 * panel, or Upcoming list — those stay Dashboard-only. Filter/sort/search
 * controls are catalog navigation, not a second dashboard.
 *
 * Widened to the same 1600px content width as Dashboard and the Objective
 * form screen (was 1040px) with a responsive auto-fit card grid, so the
 * screen actually uses the available desktop width instead of leaving most
 * of it empty.
 */
export function ObjectivesScreenDesktop({
  onOpenObjective,
  onCreateObjective,
  onBack,
}: {
  readonly onOpenObjective: (objectiveId: string) => void;
  readonly onCreateObjective: () => void;
  readonly onBack?: () => void;
}) {
  const api = useDomainApi();
  const version = useDomainVersion();
  const [objectives, setObjectives] = useState<readonly ObjectiveSummaryView[] | null>(null);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<ReadonlySet<CategoryKey>>(new Set());
  const [sortBy, setSortBy] = useState<SortBy>('target_date');

  const load = useCallback(async () => {
    const list = await api.listObjectives({ includeArchived: true });
    setObjectives(list);
  }, [api]);

  useEffect(() => {
    void load();
  }, [load, version]);

  const counts = useMemo(() => {
    const list = objectives ?? [];
    return {
      all: list.filter((o) => o.state !== 'archived').length,
      active: list.filter((o) => o.state === 'active').length,
      on_hold: list.filter((o) => o.state === 'on_hold').length,
      draft: list.filter((o) => o.state === 'draft').length,
      archived: list.filter((o) => o.state === 'archived').length,
    };
  }, [objectives]);

  const visible = useMemo(() => {
    const list = objectives ?? [];
    const byStatus = list.filter((o) => (statusFilter === 'all' ? o.state !== 'archived' : o.state === statusFilter));
    const byCategory =
      categoryFilter.size === 0
        ? byStatus
        : byStatus.filter((o) => categoryFilter.has(o.category ?? 'uncategorized'));
    const trimmedQuery = query.trim().toLowerCase();
    const byQuery = trimmedQuery ? byCategory.filter((o) => o.title.toLowerCase().includes(trimmedQuery)) : byCategory;
    return [...byQuery].sort((a, b) => {
      if (sortBy === 'alphabetical') return a.title.localeCompare(b.title);
      if (sortBy === 'progress') {
        const pa = a.progress.total > 0 ? a.progress.done / a.progress.total : 0;
        const pb = b.progress.total > 0 ? b.progress.done / b.progress.total : 0;
        return pb - pa;
      }
      return a.targetDate < b.targetDate ? -1 : a.targetDate > b.targetDate ? 1 : 0;
    });
  }, [objectives, statusFilter, categoryFilter, query, sortBy]);

  const toggleCategory = (key: CategoryKey) => {
    setCategoryFilter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const filtersActive = statusFilter !== 'all' || categoryFilter.size > 0 || query.trim().length > 0;

  return (
    <main aria-label="Objectives" style={{ padding: 'var(--space-8) var(--layout-page-margin-desktop) var(--space-16)', maxWidth: '1600px', margin: '0 auto' }}>
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to Today"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            color: 'var(--text-muted)',
            fontSize: 'var(--type-caption-size)',
            fontWeight: 600,
            minHeight: 'var(--touch-target-min)',
            marginLeft: '-8px',
            marginBottom: 'var(--space-4)',
          }}
        >
          <ChevronLeft size={16} aria-hidden="true" />
          Today
        </button>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 'var(--type-display-size)', fontWeight: 'var(--type-display-weight)', color: 'var(--text-primary)' }}>
            Objectives
          </h1>
          {objectives !== null && (
            <p style={{ marginTop: 'var(--space-1)', fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
              {objectives.length === 0 ? 'No objectives yet.' : `${objectives.length} total.`}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onCreateObjective}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            minHeight: 'var(--touch-target-min)',
            padding: '0 var(--space-4)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--hue-primary-vivid)',
            border: '1px solid var(--hue-primary-strong)',
            color: 'var(--text-on-accent)',
            fontWeight: 600,
            fontSize: 'var(--type-body-size)',
          }}
        >
          <Plus size={16} aria-hidden="true" />
          New objective
        </button>
      </div>

      {objectives !== null && objectives.length > 0 && (
        <div style={{ marginTop: 'var(--space-6)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ flex: '1 1 260px', minWidth: '200px' }}>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search objectives"
                aria-label="Search objectives"
                style={inputStyle}
              />
            </div>
            <SegmentedControl
              label="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: 'all', label: `All (${counts.all})` },
                { value: 'active', label: `Active (${counts.active})` },
                { value: 'on_hold', label: `On hold (${counts.on_hold})` },
                { value: 'draft', label: `Draft (${counts.draft})` },
                { value: 'archived', label: `Archived (${counts.archived})` },
              ]}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>
              Sort
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortBy)}
                aria-label="Sort objectives by"
                style={{
                  minHeight: 'var(--touch-target-min)',
                  padding: '0 var(--space-2)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-subtle)',
                  background: 'var(--surface-raised)',
                  color: 'var(--text-primary)',
                  fontSize: 'var(--type-caption-size)',
                }}
              >
                <option value="target_date">Target date</option>
                <option value="alphabetical">Alphabetical</option>
                <option value="progress">Progress</option>
              </select>
            </label>
          </div>

          <CategoryFilterRow selected={categoryFilter} onToggle={toggleCategory} />
        </div>
      )}

      {objectives === null ? (
        <div style={{ marginTop: 'var(--space-6)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--space-5)' }}>
          <SkeletonBlock height={220} />
          <SkeletonBlock height={220} />
          <SkeletonBlock height={220} />
        </div>
      ) : objectives.length === 0 ? (
        <p style={{ marginTop: 'var(--space-6)', fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
          Objectives give your habits somewhere to point. One or two is plenty.
        </p>
      ) : visible.length === 0 ? (
        <p style={{ marginTop: 'var(--space-6)', fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
          {filtersActive ? 'No objectives match these filters.' : 'Nothing here yet.'}
        </p>
      ) : (
        <div style={{ marginTop: 'var(--space-6)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--space-5)' }}>
          {visible.map((obj) =>
            obj.state === 'archived' ? (
              <ArchivedObjectiveCard
                key={obj.objectiveId}
                objective={obj}
                onSelect={() => onOpenObjective(obj.objectiveId)}
                onUndone={() => void load()}
              />
            ) : (
              <ObjectiveCard key={obj.objectiveId} objective={obj} onSelect={() => onOpenObjective(obj.objectiveId)} />
            ),
          )}
        </div>
      )}
    </main>
  );
}

function CategoryFilterRow({
  selected,
  onToggle,
}: {
  readonly selected: ReadonlySet<CategoryKey>;
  readonly onToggle: (key: CategoryKey) => void;
}) {
  return (
    <div role="group" aria-label="Filter by category" style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
      {CATEGORY_OPTIONS.map((option) => {
        const isSelected = selected.has(option.value);
        const Icon = option.icon;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={isSelected}
            aria-label={`Filter by category: ${option.label}`}
            onClick={() => onToggle(option.value)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-1)',
              minHeight: 'var(--touch-target-min)',
              padding: '0 var(--space-3)',
              borderRadius: 'var(--radius-pill)',
              border: isSelected ? `1px solid var(${option.tokenPrefix}-vivid)` : '1px solid var(--border-subtle)',
              background: isSelected
                ? `color-mix(in srgb, var(${option.tokenPrefix}-vivid) 14%, var(--surface-raised))`
                : 'var(--surface-raised)',
              color: isSelected ? `var(${option.tokenPrefix})` : 'var(--text-secondary)',
              fontSize: 'var(--type-caption-size)',
              fontWeight: 500,
            }}
          >
            <Icon size={14} color={isSelected ? `var(${option.tokenPrefix})` : 'var(--text-muted)'} aria-hidden="true" />
            {option.label}
          </button>
        );
      })}
      <button
        key="uncategorized"
        type="button"
        aria-pressed={selected.has('uncategorized')}
        aria-label="Filter by category: Uncategorized"
        onClick={() => onToggle('uncategorized')}
        style={{
          display: 'flex',
          alignItems: 'center',
          minHeight: 'var(--touch-target-min)',
          padding: '0 var(--space-3)',
          borderRadius: 'var(--radius-pill)',
          border: selected.has('uncategorized') ? '1px solid var(--border-strong)' : '1px solid var(--border-subtle)',
          background: selected.has('uncategorized') ? 'var(--surface-hover)' : 'var(--surface-raised)',
          color: selected.has('uncategorized') ? 'var(--text-primary)' : 'var(--text-secondary)',
          fontSize: 'var(--type-caption-size)',
          fontWeight: 500,
        }}
      >
        Uncategorized
      </button>
    </div>
  );
}

function ObjectiveCard({
  objective,
  onSelect,
}: {
  readonly objective: ObjectiveSummaryView;
  readonly onSelect: () => void;
}) {
  const complete = objective.progress.total > 0 && objective.progress.done === objective.progress.total;

  return (
    <button
      type="button"
      onClick={onSelect}
      style={{
        textAlign: 'left',
        padding: 'var(--space-5)',
        borderRadius: 'var(--radius-xl)',
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-subtle)',
        boxShadow: 'var(--shadow-1)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <span
          aria-hidden="true"
          style={{
            width: '44px',
            height: '44px',
            borderRadius: 'var(--radius-md)',
            background: 'color-mix(in srgb, var(--hue-primary-vivid) 16%, var(--surface-raised))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 'none',
          }}
        >
          <Target size={20} color="var(--hue-primary)" aria-hidden="true" />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 'var(--type-heading-size)', fontWeight: 600, color: 'var(--text-primary)' }}>
            {objective.title}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)', flex: 'none' }}>
          {objective.state === 'draft' && <StatusChip label="Draft" color="var(--text-secondary)" />}
          {objective.state === 'on_hold' && <StatusChip label="On hold" color="var(--state-caution)" />}
          {objective.state === 'active' && complete && <StatusChip label="Complete" color="var(--hue-primary)" />}
          <span
            style={{
              fontSize: 'var(--type-caption-size)',
              color: 'var(--text-secondary)',
              background: 'var(--surface-hover)',
              padding: '2px 10px',
              borderRadius: 'var(--radius-pill)',
              whiteSpace: 'nowrap',
            }}
          >
            {objective.targetDate ? `Target ${formatShortDate(objective.targetDate)}` : 'Ongoing'}
          </span>
        </div>
      </div>

      <div
        className="num"
        style={{ marginTop: 'var(--space-4)', fontSize: 'var(--type-caption-size)', color: 'var(--hue-primary)', fontWeight: 600 }}
      >
        {objective.progress.done} / {objective.progress.total} complete
      </div>
      <RailProgressLine done={objective.progress.done} total={objective.progress.total} />
    </button>
  );
}

function StatusChip({ label, color }: { readonly label: string; readonly color: string }) {
  return (
    <span
      style={{
        fontSize: 'var(--type-caption-size)',
        fontWeight: 600,
        color,
        background: 'var(--surface-hover)',
        padding: '2px 10px',
        borderRadius: 'var(--radius-pill)',
      }}
    >
      {label}
    </span>
  );
}

/**
 * Archived-register card: quiet — muted icon tile, no target chip, static
 * progress fact instead of a live bar, no forecast box ever (a hard rule,
 * not conditional). Exposes the "Undo" text action, reading the winning
 * archive event's ID directly off `ObjectiveSummaryView.archivedEventId`
 * (architect-landed, mirroring `ObjectiveForecastView.
 * appliedAdjustmentEventId`'s established pattern) — no `getAllEvents()`
 * scan needed.
 */
function ArchivedObjectiveCard({
  objective,
  onSelect,
  onUndone,
}: {
  readonly objective: ObjectiveSummaryView;
  readonly onSelect: () => void;
  readonly onUndone: () => void;
}) {
  const api = useDomainApi();
  const [undoing, setUndoing] = useState(false);

  return (
    <div
      style={{
        textAlign: 'left',
        padding: 'var(--space-5)',
        borderRadius: 'var(--radius-xl)',
        background: 'var(--surface-base)',
        border: '1px solid var(--border-subtle)',
      }}
    >
      <button type="button" onClick={onSelect} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', width: '100%', textAlign: 'left' }}>
        <span
          aria-hidden="true"
          style={{
            width: '44px',
            height: '44px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--surface-hover)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 'none',
          }}
        >
          <Target size={20} color="var(--text-muted)" aria-hidden="true" />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 'var(--type-heading-size)', fontWeight: 600, color: 'var(--text-secondary)' }}>
            {objective.title}
          </div>
          <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginTop: 'var(--space-1)' }}>
            Archived
          </div>
        </div>
      </button>

      <p style={{ marginTop: 'var(--space-3)', fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
        {objective.progress.done} of {objective.progress.total} complete when archived
      </p>

      {objective.archivedEventId && (
        <button
          type="button"
          disabled={undoing}
          onClick={() => {
            setUndoing(true);
            void api.revertLogEntry(objective.archivedEventId as never).then(onUndone);
          }}
          style={{
            marginTop: 'var(--space-2)',
            color: 'var(--text-secondary)',
            textDecoration: 'underline',
            fontWeight: 400,
            fontSize: 'var(--type-caption-size)',
            minHeight: 'var(--touch-target-min)',
          }}
        >
          Undo
        </button>
      )}
    </div>
  );
}
